import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { confirmAction } from "../lib/confirm"

// Phase 8 — Generic Camera management in the project Integrations tab.
// Mirrors project_integration_links_controller's flash-and-reload +
// toast + CSRF conventions so the two surfaces behave identically.
export default class extends Controller {
  static targets = [
    "modal", "form", "cameraId", "title",
    "name", "url", "show", "thumb", "snap",
    "interval", "start", "snapshotFields",
    "frequency", "secondHour", "secondHourWrapper",
    "thumbWarning"
  ]
  static values = {
    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String
  }

  connect() {
    this._showPendingFlash()
  }

  openCreate() {
    this.cameraIdTarget.value = ""
    this.titleTarget.textContent = "Add camera"
    this.formTarget.reset()
    this.showTarget.checked = false
    this.thumbTarget.checked = false
    this.snapTarget.checked = false
    if (this.hasIntervalTarget) this.intervalTarget.value = "24"
    this.startTarget.value = "7"
    if (this.hasFrequencyTarget) this.frequencyTarget.value = "daily"
    if (this.hasSecondHourTarget) this.secondHourTarget.value = "19"
    this.toggleSnapshotFields()
    this.toggleSecondHour()
    this.evaluateUrl()
    this._open()
  }

  openEdit(event) {
    const d = event.currentTarget.dataset
    this.cameraIdTarget.value = d.cameraId || ""
    this.titleTarget.textContent = "Edit camera"
    this.nameTarget.value = d.name || ""
    this.urlTarget.value = d.url || ""
    this.showTarget.checked = d.show === "true"
    this.thumbTarget.checked = d.thumb === "true"
    this.snapTarget.checked = d.snap === "true"
    if (d.interval && this.hasIntervalTarget) this.intervalTarget.value = d.interval
    if (d.start) this.startTarget.value = d.start
    // Existing rows with a stored second hour open as "Twice a day";
    // anything else opens as "Once a day".
    const hasSecond = d.second && d.second !== ""
    if (this.hasFrequencyTarget) {
      this.frequencyTarget.value = hasSecond ? "twice_daily" : "daily"
    }
    if (hasSecond && this.hasSecondHourTarget) {
      this.secondHourTarget.value = d.second
    }
    this.toggleSnapshotFields()
    this.toggleSecondHour()
    this.evaluateUrl()
    this._open()
  }

  toggleSnapshotFields() {
    if (!this.hasSnapshotFieldsTarget) return
    this.snapshotFieldsTarget.classList.toggle("opacity-40", !this.snapTarget.checked)
    this.snapshotFieldsTarget.classList.toggle("pointer-events-none", !this.snapTarget.checked)
  }

  toggleSecondHour() {
    if (!this.hasSecondHourWrapperTarget || !this.hasFrequencyTarget) return
    const show = this.frequencyTarget.value === "twice_daily"
    this.secondHourWrapperTarget.classList.toggle("hidden", !show)
  }

  // Live URL evaluation — mirrors ProjectCamera#thumbnail_render_kind.
  // Shows a small warning under the URL field when the link won't
  // render as a thumbnail (HLS / WebRTC / RTSP / unknown). Does not
  // block submit — auto-snapshot and live feed display are
  // independent, so the camera may still be useful as a snapshot
  // source.
  evaluateUrl() {
    if (!this.hasThumbWarningTarget || !this.hasUrlTarget) return
    const value = (this.urlTarget.value || "").trim()
    if (value.length === 0) {
      this.thumbWarningTarget.classList.add("hidden")
      return
    }
    const renderable = this._urlIsRenderable(value)
    this.thumbWarningTarget.classList.toggle("hidden", renderable)
  }

  _urlIsRenderable(raw) {
    let parsed
    try { parsed = new URL(raw) } catch (_) { return false }
    const scheme = (parsed.protocol || "").replace(":", "").toLowerCase()
    if (scheme !== "http" && scheme !== "https") return false

    const host = (parsed.hostname || "").toLowerCase()
    const path = parsed.pathname || ""

    // YouTube
    if (host === "youtu.be" || host.endsWith(".youtu.be")) {
      return path.replace(/^\//, "").length > 0
    }
    if (host.includes("youtube.com")) {
      if (path === "/watch") return !!parsed.searchParams.get("v")
      if (path.startsWith("/live/") || path.startsWith("/shorts/") || path.startsWith("/embed/")) return true
    }
    // Vimeo
    if (host === "vimeo.com" || host.endsWith(".vimeo.com")) {
      if (host === "player.vimeo.com") return true
      const id = path.replace(/^\//, "").split("/")[0]
      if (/^\d+$/.test(id)) return true
    }
    // Image / MJPEG extension
    const ext = (path.split(".").pop() || "").toLowerCase()
    if (["jpg","jpeg","png","gif","webp","bmp","svg","mjpg","mjpeg"].includes(ext)) return true

    return false
  }

  closeForm() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submitForm(event) {
    event.preventDefault()
    const id = this.cameraIdTarget.value
    const url = id
      ? this.updateUrlTemplateValue.replace("ID", id)
      : this.createUrlValue
    const method = id ? "PATCH" : "POST"

    try {
      const res = await fetch(url, {
        method,
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: new FormData(this.formTarget)
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._flashAndReload(data.message || "Camera saved.", "notice")
      } else {
        showToast(data.message || "Couldn't save the camera. Try again.", "error")
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
    }
  }

  async confirmDelete(event) {
    const id = event.currentTarget.dataset.cameraId
    const name = event.currentTarget.dataset.name || "this camera"
    if (!id) return
    const ok = await confirmAction({
      title: "Remove camera?",
      message: `Remove ${name} from this project? Existing snapshots already saved to Photos are kept.`,
      confirmLabel: "Yes, remove",
      danger: true
    })
    if (ok) this._doDelete(id)
  }

  async _doDelete(id) {
    const url = this.destroyUrlTemplateValue.replace("ID", id)
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._flashAndReload(data.message || "Camera removed.", "notice")
      } else {
        showToast(data.message || "Couldn't remove the camera. Try again.", "error")
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
    }
  }

  _open() {
    this.modalTarget.classList.remove("hidden")
  }

  _flashAndReload(message, type = "notice") {
    try {
      sessionStorage.setItem("__pilFlash", JSON.stringify({ message, type }))
    } catch (e) { /* ignore */ }
    window.location.reload()
  }

  _showPendingFlash() {
    try {
      const raw = sessionStorage.getItem("__pilFlash")
      if (!raw) return
      sessionStorage.removeItem("__pilFlash")
      const parsed = JSON.parse(raw)
      showToast(parsed.message, parsed.type === "alert" ? "error" : "success")
    } catch (e) { /* ignore */ }
  }

  _csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
